import { LightBox } from "@jmc/solid-design-system/src/components/molecules/LightBox/LightBox";
import React, { ReactElement, useState } from "react";
import style from "./style.module.scss";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { mdiFullscreen } from "@mdi/js";
import classnames from "classnames";

interface PropTypes {
    children: JSX.Element | JSX.Element[];
    enableLightBox?: boolean;
    fit?: "cover" | "contain" | "fill";
    height?: number;
    enlargeLabel?: string;
}

interface ImageProps {
    url: string;
    alt: string;
    title?: string;
    caption?: string;
    dimension?: { height: number; width: number };
}

export const Image: React.FunctionComponent<ImageProps> = () => {
    return <></>;
};

/**
 * ImageGallery component renders any children in a flexbox with Lightbox functionality .
 * Typically used to wrap the GatsbyImage component from gatsby-plugin-image.
 */
export const Gallery: React.FunctionComponent<PropTypes> = (props: PropTypes) => {
    const [showLightBox, setShowLightBox] = useState(false);
    const [defaultLightBoxImage, setDefaultLightBoxImage] = useState(0);

    const openLightBox = (index: number) => {
        const { enableLightBox } = props;
        if (enableLightBox) {
            setDefaultLightBoxImage(index);
            setShowLightBox(true);
        }
    };
    const { enableLightBox = true, children, enlargeLabel = null } = props;

    return (
        <div className={style.element} data-test-id="ImageGallery">
            <div className={style.flexContainer}>
                {React.Children.map(children, (child: ReactElement, index: number) => {
                    const width = child.props?.image?.width || 1;
                    const height = child.props?.image?.height || 1;

                    return (
                        <div
                            key={index}
                            onClick={() => openLightBox(index)}
                            onKeyDown={(e: KeyboardEvent): void => keyHandler(e.key, () => openLightBox(index))}
                            data-test-id="ImageGallery.Image.Image"
                            className={classnames(
                                style.image,
                                enableLightBox && !showLightBox ? style.clickable : null,
                            )}
                            style={{
                                width: (width / height) * 200,
                                flexGrow: (width / height) * 200,
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            {child}
                            {enableLightBox && width && (
                                <button
                                    className={style.lightBoxIcon}
                                    onClick={() => openLightBox(index)}
                                    tabIndex={0}
                                    aria-label={enlargeLabel}
                                >
                                    <Icon icon={mdiFullscreen} size="large" color="inherit" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {showLightBox && (
                <LightBox defaultSelectedImage={defaultLightBoxImage} onClose={(): void => setShowLightBox(false)}>
                    {React.Children.count(children) === 1 ? (
                        <LightBox.Image
                            key={(children as React.ReactElement[])?.[0]?.props?.image?.images?.fallback?.src}
                            url={(children as React.ReactElement[])?.[0]?.props?.image?.images?.fallback?.src}
                            title={(children as React.ReactElement[])?.[0]?.props?.title}
                        />
                    ) : (
                        React.Children.map(children, (child: JSX.Element) => {
                            return (
                                <LightBox.Image
                                    key={child.props?.image?.images?.fallback?.src}
                                    url={child.props?.image?.images?.fallback?.src}
                                    title={child?.props?.title}
                                />
                            );
                        })
                    )}
                </LightBox>
            )}
        </div>
    );
};
